import { mutate } from "swr";
import { useFetcher } from "~/hooks/useFetcher";
import {
  type AccessLevelOption,
  type ShareCollaborators,
  type ShareGroup,
  type ShareUser,
} from "~/pages/manifold-nextgen/components/share/types";
import {
  AgentGrantHolder,
  AgentGrantList,
  AgentGrantMember,
  AgentRole,
  agentGrantsPath,
  grantAgentRole,
  groupPrincipal,
  parsePrincipal,
  revokeAgentRole,
  userPrincipal,
} from "~/services/agents";

// Listed most- to least-permissive: AccessDetailsView ranks a user's effective
// access by this key order when they hold a role both directly and via a group.
const accessLevels: Record<string, AccessLevelOption> = {
  [AgentRole.IS_MANAGER]: {
    label: "Manager",
    description: "Full access, including editing and sharing this agent",
  },
  [AgentRole.IS_EDITOR]: {
    label: "Editor",
    description: "Can use and edit this agent",
  },
  [AgentRole.IS_USER]: {
    label: "User",
    description: "Can use this agent",
  },
};

const holderFullName = (holder: AgentGrantHolder, email: string): string =>
  [holder.given_name, holder.family_name].filter(Boolean).join(" ") || email;

const memberFullName = (member: AgentGrantMember): string =>
  [member.given_name, member.family_name].filter(Boolean).join(" ") || member.email;

// The grant listing splits into users and groups, each carrying its role as a
// plain access-level string. Group holders carry the resolved display name and
// their expanded members, who inherit the group's role; user holders carry the
// resolved name and avatar. Enrichment is absent on the grant (`POST`) response,
// so each field falls back to the bare email/tag.
export const toAgentCollaborators = (holders: AgentGrantHolder[]): ShareCollaborators => {
  const users: ShareUser[] = [];
  const groups: ShareGroup[] = [];
  for (const holder of holders) {
    const { isGroup, id } = parsePrincipal(holder.principal);
    if (isGroup) {
      groups.push({
        name: id,
        display_name: holder.display_name ?? id,
        is_internal: holder.is_internal ?? false,
        access_level: holder.role,
        users: (holder.members ?? []).map((member) => ({
          email: member.email,
          full_name: memberFullName(member),
          image_url: member.image_url ?? undefined,
          access_level: holder.role,
        })),
      });
    } else {
      users.push({
        email: id,
        full_name: holderFullName(holder, id),
        image_url: holder.image_url ?? undefined,
        access_level: holder.role,
      });
    }
  }
  return { users, groups };
};

// Pass `enabled: false` to skip the fetch — e.g. for SYSTEM agents, which are
// not shared per-principal through this UI.
export const useAgentShare = (agentId: string, enabled = true) => {
  const {
    data,
    isLoading,
    error: collaboratorsError,
  } = useFetcher<AgentGrantList>(enabled ? agentGrantsPath(agentId) : null);

  const collaborators = data ? toAgentCollaborators(data.items) : undefined;

  // A principal holds at most one role here, so a role change is a revoke of the
  // old role followed by a grant of the new; both endpoints are idempotent.
  // Revoke first so a principal never transiently holds two roles.
  const handleShare = async (
    recipient: { user?: ShareUser; group?: ShareGroup },
    accessLevel: string | null
  ) => {
    const { user, group } = recipient;
    const principal = user ? userPrincipal(user.email) : group ? groupPrincipal(group.name) : null;
    if (!principal) {
      return;
    }

    const currentRole =
      (user
        ? collaborators?.users.find((u) => u.email.toLowerCase() === user.email.toLowerCase())
        : collaborators?.groups.find((g) => g.name === group?.name)
      )?.access_level ?? null;

    if (accessLevel === null) {
      if (currentRole) {
        await revokeAgentRole(agentId, principal, currentRole as AgentRole);
      }
    } else {
      if (currentRole && currentRole !== accessLevel) {
        await revokeAgentRole(agentId, principal, currentRole as AgentRole);
      }
      await grantAgentRole(agentId, principal, accessLevel as AgentRole);
    }
    await mutate(agentGrantsPath(agentId));
  };

  return {
    collaborators,
    isLoading,
    collaboratorsError,
    handleShare,
    accessLevels,
    defaultAccessLevel: AgentRole.IS_USER as string,
  };
};
